import axios from 'axios';
import toast from 'react-hot-toast';
import { sesionEmbebida, estaEmbebido, avisarSesionVencida } from './sesionEmbebida';

// Create axios instance for internal API (dashboard)
const apiBaseUrl = import.meta.env.VITE_API_URL || '';
const api = axios.create({
  baseURL: `${apiBaseUrl}/internal`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token and tenant slug
api.interceptors.request.use(
  (config) => {
    // Embebido en otro producto: la sesión vive solo en memoria y NO en
    // `localStorage`, para no cambiarle la sesión al usuario en sus otras
    // pestañas (mismo origen). Ver `utils/sesionEmbebida.js`.
    const embebida = sesionEmbebida();
    if (embebida) {
      config.headers.Authorization = `Bearer ${embebida.token}`;
      if (embebida.tenantSlug) config.headers['X-Tenant-Slug'] = embebida.tenantSlug;
      if (embebida.emisorId) config.headers['X-Emisor-ID'] = embebida.emisorId;
      return config;
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant slug
    const tenantSlug = localStorage.getItem('tenantSlug');
    if (tenantSlug) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }

    // Add emisor ID if selected
    const emisorId = localStorage.getItem('emisorId');
    if (emisorId) {
      config.headers['X-Emisor-ID'] = emisorId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple redirects
let isRedirecting = false;

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred';

    // Embebido: un 401 NO manda a `/login`. Pintar un login dentro del iframe
    // es justo el defecto que se ve hoy entre Finance y Payroll, y además
    // borraría la sesión buena que el usuario tiene en sus otras pestañas. Se le
    // avisa al anfitrión, que pide otro boleto y vuelve a poner la sesión.
    if (error.response?.status === 401 && estaEmbebido()) {
      if (avisarSesionVencida()) return Promise.reject(error);
    }

    // Handle 401 - redirect to login (only once)
    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true;
      // Clear all auth data including Zustand persist storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('emisorId');
      localStorage.removeItem('tenantSlug');
      localStorage.removeItem('auth-storage'); // Zustand persist key
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Don't show toast for: redirecting, 404, or aborted/canceled requests
    if (!isRedirecting && error.response?.status !== 404 && error.code !== 'ERR_CANCELED' && error.code !== 'ECONNABORTED') {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password, tenantSlug) => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    return axios.post(`${apiUrl}/internal/auth/login`, { email, password }, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    });
  },

  me: () => api.get('/auth/me'),

  // Password recovery (rutas públicas, requieren tenant header)
  forgotPassword: (email, tenantSlug) => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    return axios.post(`${apiUrl}/internal/auth/forgot-password`, { email }, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    });
  },
  validateResetToken: (token, tenantSlug) => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    return axios.get(`${apiUrl}/internal/auth/reset-password/${token}`, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    });
  },
  resetPassword: (token, password, tenantSlug) => {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    return axios.post(`${apiUrl}/internal/auth/reset-password`, { token, password }, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    });
  },
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentDocuments: () => api.get('/dashboard/recent'),
  getMonthlyStats: (months = 6) => api.get(`/dashboard/monthly-stats?months=${months}`),

  // AI Analytics endpoints
  getAISummary: () => api.get('/dashboard/ai/summary'),
  getCashFlow: (days = 30) => api.get(`/dashboard/ai/cash-flow?days=${days}`),
  getAlerts: () => api.get('/dashboard/ai/alerts'),
  getClientRisk: () => api.get('/dashboard/ai/risk/clients'),
  getSupplierAnalysis: () => api.get('/dashboard/ai/risk/suppliers'),
  getKPIs: () => api.get('/dashboard/ai/kpis'),
  getAnomalies: () => api.get('/dashboard/ai/anomalies'),
};

// Helper to clean params (remove undefined/null values)
const cleanParams = (params) => {
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

// Documents API
export const documentsApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/documents${queryString ? `?${queryString}` : ''}`);
  },
  // Corrección de descripción de línea en documentos ya emitidos (permiso
  // concedido a mano; cambia solo el PDF, nunca el XML firmado)
  puedeCorregirDescripcion: () => api.get('/documents/correccion-descripcion/permitido'),
  listarCorrecciones: (id) => api.get(`/documents/${id}/correcciones`),
  corregirDescripcion: (id, lineaId, data) => api.put(`/documents/${id}/lineas/${lineaId}/descripcion`, data),

  exportExcel: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/documents/export/excel${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  exportPdf: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/documents/export/pdf${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  getById: (id) => api.get(`/documents/${id}`),

  create: (data, submit = true) => api.post('/documents', { ...data, submit }),

  update: (id, data) => api.put(`/documents/${id}`, data),

  submit: (id, opciones) => api.post(`/documents/${id}/submit`, opciones || {}),

  delete: (id) => api.delete(`/documents/${id}`),

  sendEmail: (id, options = {}) => api.post(`/documents/${id}/email`, options),

  copy: (id) => api.post(`/documents/${id}/copy`),

  cancel: (id, razon) => api.post(`/documents/${id}/cancel`, { razon }),

  // Eliminar documento cancelado (solo si el consecutivo fue reutilizado por otro aceptado)
  deleteCancelled: (id) => api.delete(`/documents/${id}/cancelled`),

  downloadXml: (id) => api.get(`/documents/${id}/xml`, { responseType: 'blob' }),

  downloadPdf: (id) => api.get(`/documents/${id}/pdf`, { responseType: 'blob' }),

  approveRecurring: (id) => api.post(`/documents/${id}/approve-recurring`),

  // Validar CABYS de líneas vs exoneración del receptor
  validateExoneracion: (receptorId, lineas) => api.post('/documents/validate-exoneracion', { receptorId, lineas }),
};

// Reportes API
// API Drafts (Facturación API)
export const apiDraftsApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/api-drafts${queryString ? `?${queryString}` : ''}`);
  },
  approve: (id, body = {}) => api.post(`/api-drafts/${id}/approve`, body),
  revision: (id) => api.get(`/api-drafts/${id}/revision`),
  unapprove: (id) => api.post(`/api-drafts/${id}/unapprove`),
  reject: (id, reason) => api.post(`/api-drafts/${id}/reject`, { reason }),
  batchApprove: (ids) => api.post('/api-drafts/batch/approve', { ids }),
  softDelete: (ids) => api.post('/api-drafts/batch/soft-delete', { ids }),
  restore: (id) => api.post(`/api-drafts/${id}/restore`),
  // Config de alertas por correo
  getAlertConfig: () => api.get('/api-drafts/alert-config'),
  saveAlertConfig: (data) => api.put('/api-drafts/alert-config', data),
  sendAlertTest: () => api.post('/api-drafts/alert-config/test'),
};

export const reportesApi = {
  // Impuestos especiales (911 y Cruz Roja)
  getImpuestosEspeciales: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/impuestos-especiales${queryString ? `?${queryString}` : ''}`);
  },
  exportImpuestosEspeciales: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/impuestos-especiales/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
  exportImpuestosEspecialesPdf: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/impuestos-especiales/pdf${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Resumen de Ventas (PDF firmado por Contador y Representante Legal)
  resumenVentasPdf: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/resumen-ventas/pdf${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
  resumenVentasExcel: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/resumen-ventas/excel${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Reportes Personalizados (constructor)
  reportePersonalizadoPreview: (config = {}) => api.post('/reportes/personalizado/preview', config),
  reportePersonalizadoPdf: (config = {}) => api.post('/reportes/personalizado/pdf', config, { responseType: 'blob' }),
  reportePersonalizadoExcel: (config = {}) => api.post('/reportes/personalizado/excel', config, { responseType: 'blob' }),

  // Diferencial Cambiario
  getDiferencialCambiario: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/diferencial-cambiario${queryString ? `?${queryString}` : ''}`);
  },
  exportDiferencialCambiario: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/diferencial-cambiario/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Retenciones 2% Renta (saldo a favor)
  getRetencionesRenta: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/retenciones-renta${queryString ? `?${queryString}` : ''}`);
  },
  exportRetencionesRenta: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/retenciones-renta/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Ventas por Período
  getVentasPeriodo: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-periodo${queryString ? `?${queryString}` : ''}`);
  },
  exportVentasPeriodo: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-periodo/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Ventas por Cliente
  getVentasCliente: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-cliente${queryString ? `?${queryString}` : ''}`);
  },
  exportVentasCliente: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-cliente/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
  exportVentasClientePdf: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-cliente/pdf${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Ventas por Producto
  getVentasProducto: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-producto${qs ? `?${qs}` : ''}`);
  },
  exportVentasProducto: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-producto/export${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },
  exportVentasProductoPdf: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/ventas-producto/pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },

  // IVA Ventas (Débito) — D-104
  getIvaVentasDebito: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/iva-ventas-debito${qs ? `?${qs}` : ''}`);
  },
  exportIvaVentasDebito: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/iva-ventas-debito/export${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },
  exportIvaVentasDebitoPdf: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/iva-ventas-debito/pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },

  // Cuentas por Cobrar (Antigüedad de Saldos)
  getCuentasCobrar: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/cuentas-cobrar${queryString ? `?${queryString}` : ''}`);
  },
  exportCuentasCobrar: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/cuentas-cobrar/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
  exportCuentasCobrarPdf: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/reportes/cuentas-cobrar/pdf${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
};

// Emisores API
export const emisoresApi = {
  getMy: () => api.get('/my/emisores'),
  setDefault: (emisorId) => api.post('/my/emisores/default', { emisorId }),
  create: (data) => api.post('/emisores', data),
  // Consultar contribuyente por cédula en Hacienda
  consultarHacienda: (identificacion) => api.get(`/hacienda/contribuyente/${identificacion}`),
};

// Receptores API
export const receptoresApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/receptores${queryString ? `?${queryString}` : ''}`);
  },
  search: (query) => api.get(`/receptores?q=${encodeURIComponent(query)}`),
  getById: (id) => api.get(`/receptores/${id}`),
  create: (data) => api.post('/receptores', data),
  update: (id, data) => api.put(`/receptores/${id}`, data),
  updateActividad: (id, actividad) => api.patch(`/receptores/${id}/actividad`, actividad),
  delete: (id) => api.delete(`/receptores/${id}`),
  import: (clientes) => api.post('/receptores/import', { clientes }),
  // Consultar contribuyente en Hacienda
  consultarHacienda: (identificacion) => api.get(`/receptores/consultar/${identificacion}`),
  // Consultar exoneración en Hacienda
  consultarExoneracion: (numeroAutorizacion) => api.get(`/receptores/exoneracion/${encodeURIComponent(numeroAutorizacion)}`),
  // Obtener información de crédito del cliente
  getCreditInfo: (id) => api.get(`/receptores/${id}/credito`),
  // Exoneraciones múltiples
  getExoneraciones: (id) => api.get(`/receptores/${id}/exoneraciones`),
  getActiveExoneraciones: (id) => api.get(`/receptores/${id}/exoneraciones/active`),
  createExoneracion: (id, data) => api.post(`/receptores/${id}/exoneraciones`, data),
  updateExoneracion: (receptorId, exoId, data) => api.put(`/receptores/${receptorId}/exoneraciones/${exoId}`, data),
  deleteExoneracion: (receptorId, exoId) => api.delete(`/receptores/${receptorId}/exoneraciones/${exoId}`),
};

// Settings API
// Storage config (Local vs Azure Blob por tenant)
export const storageApi = {
  get: () => api.get('/storage-config'),
  update: (data) => api.put('/storage-config', data),
  test: () => api.post('/storage-config/test'),
  stats: () => api.get('/storage-config/stats'),
};

export const settingsApi = {
  // Hacienda config
  getHacienda: () => api.get('/settings/hacienda'),
  getHaciendaConfig: () => api.get('/settings/hacienda'), // alias
  saveHacienda: (data) => api.put('/settings/hacienda', data),
  updateHaciendaConfig: (data) => api.put('/settings/hacienda', data), // alias
  testHacienda: () => api.post('/settings/hacienda/test'),
  testHaciendaConnection: () => api.post('/settings/hacienda/test'), // alias

  // Certificates
  getCertificates: () => api.get('/settings/certificates'),
  uploadCertificate: (formData) => api.post('/settings/certificates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  activateCertificate: (id) => api.post(`/settings/certificates/${id}/activate`),
  deleteCertificate: (id) => api.delete(`/settings/certificates/${id}`),

  // Consecutivos
  getConsecutivos: () => api.get('/settings/consecutivos'),
  getConsecutivosReciclados: () => api.get('/settings/consecutivos-reciclados'),
  deleteConsecutivoReciclado: (id) => api.delete(`/settings/consecutivos-reciclados/${id}`),
  deleteAllConsecutivosReciclados: () => api.delete('/settings/consecutivos-reciclados'),
  updateConsecutivos: (data) => api.put('/settings/consecutivos', data),

  // Emisor/Empresa data
  getEmisor: () => api.get('/settings/emisor'),
  updateEmisor: (data) => api.put('/settings/emisor', data),

  // Email config (Office 365 / Azure AD)
  getEmailConfig: () => api.get('/settings/email'),
  saveEmailConfig: (data) => api.put('/settings/email', data),
  testEmailConnection: () => api.post('/settings/email/test'),
  sendTestEmail: (testEmail) => api.post('/settings/email/send-test', { testEmail }),
  deleteEmailConfig: () => api.delete('/settings/email'),

  // Cleanup encrypted data (for fixing encryption key issues)
  cleanupEncrypted: () => api.delete('/settings/cleanup-encrypted'),

  // Tenant Config (global settings for the tenant)
  getTenantConfig: () => api.get('/settings/tenant-config'),
  updateTenantConfig: (config) => api.put('/settings/tenant-config', { config }),

  // Exchange rate (BCCR - Banco Central de Costa Rica)
  // Public endpoint (no auth needed)
  getExchangeRate: () => axios.get('/exchange-rate'),
};

// Planillas (Payroll360 Lite) — SSO embed
export const planillasApi = {
  getSsoUrl: () => api.get('/planillas/sso-url')
};

// Notificaciones in-app
export const notificationsApi = {
  list: (params = {}) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  // Admin: subscribers
  listSubscribers: () => api.get('/notif-subscribers'),
  updateSubscriber: (userId, tipo, suscrito) =>
    api.put('/notif-subscribers', { userId, tipo, suscrito })
};

// Productos API
export const productosApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/productos${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/productos/${id}`),
  create: (data) => api.post('/productos', data),
  update: (id, data) => api.put(`/productos/${id}`, data),
  delete: (id) => api.delete(`/productos/${id}`),
  import: (productos) => api.post('/productos/import', { productos }),
  masiveUpdate: (data) => api.post('/productos/masive-update', data),
  // Copiar producto del emisor actual a otros emisores del mismo tenant.
  // Replica TODO: códigos, descripción, CABYS, unidad, precio, impuestos, tarifa IVA.
  copyToEmisor: (id, targetEmisorIds) => api.post(`/productos/${id}/copy-to-emisor`, { targetEmisorIds }),
  // CABYS - local DB with Hacienda sync
  searchCabys: (query) => api.get(`/cabys/search?q=${encodeURIComponent(query)}`),
  getCabysCategories: () => api.get('/cabys/categories'),
  getCabysByCategory: (code) => api.get(`/cabys/category/${code}`),
  getCabysSyncStatus: () => api.get('/cabys/sync/status'),
  syncCabys: () => api.post('/cabys/sync'),
};

// ====================
// GASTOS / CXP API
// ====================
export const gastosApi = {
  // Proveedores
  listProveedores: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/proveedores${queryString ? `?${queryString}` : ''}`);
  },
  searchProveedores: (q) => api.get(`/gastos/proveedores?q=${encodeURIComponent(q)}`),
  getProveedor: (id) => api.get(`/gastos/proveedores/${id}`),
  createProveedor: (data) => api.post('/gastos/proveedores', data),
  updateProveedor: (id, data) => api.put(`/gastos/proveedores/${id}`, data),
  deleteProveedor: (id) => api.delete(`/gastos/proveedores/${id}`),

  // Grupos de Proveedores
  listGruposProveedores: () => api.get('/gastos/grupos-proveedores'),
  getGrupoProveedor: (id) => api.get(`/gastos/grupos-proveedores/${id}`),
  createGrupoProveedor: (data) => api.post('/gastos/grupos-proveedores', data),
  updateGrupoProveedor: (id, data) => api.put(`/gastos/grupos-proveedores/${id}`, data),
  deleteGrupoProveedor: (id) => api.delete(`/gastos/grupos-proveedores/${id}`),
  setGrupoMiembros: (id, proveedorIds) => api.post(`/gastos/grupos-proveedores/${id}/miembros`, { proveedorIds }),

  // Facturas de Compra
  listFacturas: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/facturas${queryString ? `?${queryString}` : ''}`);
  },
  getFacturasSummary: () => api.get('/gastos/facturas/summary'),
  getFactura: (id) => api.get(`/gastos/facturas/${id}`),
  downloadXML: (id) => api.get(`/gastos/facturas/${id}/xml`, { responseType: 'blob' }),
  downloadPDF: (id) => api.get(`/gastos/facturas/${id}/pdf`, { responseType: 'blob' }),
  exportExcel: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/facturas/export/excel${queryString ? `?${queryString}` : ''}`, { responseType: 'blob' });
  },
  exportPdf: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/facturas/export/pdf${queryString ? `?${queryString}` : ''}`, { responseType: 'blob' });
  },
  createFactura: (data) => api.post('/gastos/facturas', data),
  updateFactura: (id, data) => api.put(`/gastos/facturas/${id}`, data),
  updateFacturaEstado: (id, data) => api.patch(`/gastos/facturas/${id}/estado`, data),
  prepararConfirmacion: (ids) => api.get('/gastos/facturas/confirmacion/preparar', { params: { ids: ids.join(',') } }),
  getCumplimiento: (id) => api.get(`/gastos/facturas/${id}/cumplimiento`),
  getCamposFE44: () => api.get('/gastos/recepcion/campos'),
  getPendientesRechazo: () => api.get('/gastos/recepcion/pendientes'),
  pasarAPendientes: (data) => api.post('/gastos/recepcion/pendientes', data),
  getCorreoProveedor: ({ facturaId, pendienteId, tipo }) => api.get('/gastos/recepcion/correo-proveedor', { params: { facturaId, pendienteId, tipo } }),
  enviarCorreoPendiente: (id, data) => api.post(`/gastos/recepcion/pendientes/${id}/correo`, data),
  aceptarConNC: (id) => api.post(`/gastos/recepcion/pendientes/${id}/aceptar-con-nc`),
  rechazarPendiente: (id) => api.post(`/gastos/recepcion/pendientes/${id}/rechazar`),
  ncCandidatas: (id) => api.get(`/gastos/recepcion/pendientes/${id}/nc-candidatas`),
  vincularNC: (id, ncId) => api.post(`/gastos/recepcion/pendientes/${id}/vincular-nc`, { ncId }),
  getPlantillaRecepcion: (tipo, emisorId) => api.get(`/gastos/recepcion/plantilla/${tipo}`, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  guardarPlantillaRecepcion: (tipo, data, emisorId) => api.put(`/gastos/recepcion/plantilla/${tipo}`, data, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  vistaPreviaPlantillaRecepcion: (tipo, data, emisorId) => api.post(`/gastos/recepcion/plantilla/${tipo}/vista-previa`, data, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  getRecepcionConfig: (emisorId) => api.get('/gastos/recepcion/config', emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  updateRecepcionConfig: (data, emisorId) => api.put('/gastos/recepcion/config', data, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  updateRecepcionRegla: (id, data, emisorId) => api.put(`/gastos/recepcion/reglas/${id}`, data, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  crearRecepcionRegla: (data, emisorId) => api.post('/gastos/recepcion/reglas', data, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  borrarRecepcionRegla: (id, emisorId) => api.delete(`/gastos/recepcion/reglas/${id}`, emisorId ? { headers: { 'X-Emisor-ID': emisorId } } : undefined),
  checkMensajeReceptorStatus: (facturaId) => api.get(`/gastos/facturas/${facturaId}/mensaje-receptor/status`),
  deleteFactura: (id) => api.delete(`/gastos/facturas/${id}`),

  // Upload
  uploadXML: (formData) => api.post('/gastos/facturas/upload-xml', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Importación múltiple (lote): XML de la factura + respuesta de Hacienda + PDF
  loteCrear: () => api.post('/gastos/facturas/lote'),
  loteSubir: (loteId, formData, onUploadProgress) => api.post(`/gastos/facturas/lote/${loteId}/archivos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress, timeout: 300000
  }),
  loteAnalizar: (loteId) => api.get(`/gastos/facturas/lote/${loteId}`, { timeout: 300000 }),
  loteAsignarPdf: (loteId, pdf, clave) => api.post(`/gastos/facturas/lote/${loteId}/asignar-pdf`, { pdf, clave }),
  loteImportar: (loteId, clave) => api.post(`/gastos/facturas/lote/${loteId}/importar/${clave}`, {}, { timeout: 120000 }),
  loteBorrar: (loteId) => api.delete(`/gastos/facturas/lote/${loteId}`),
  loteArchivo: (loteId, interno) => api.get(`/gastos/facturas/lote/${loteId}/archivo/${interno}`, { responseType: 'blob', timeout: 60000 }),
  uploadPDF: (formData) => api.post('/gastos/facturas/upload-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  extractPDF: (formData) => api.post('/gastos/facturas/extract-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Aplicar NC
  aplicarNC: (ncId, facturas, notas) => api.post('/gastos/facturas/aplicar-nc', Array.isArray(facturas) ? { ncId, facturaIds: facturas, notas } : { ncId, facturaId: facturas, notas }),

  // Pagos
  listPagos: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/pagos${queryString ? `?${queryString}` : ''}`);
  },
  aplicarPago: (facturaId, data, comprobante = null) => {
    // If data is already FormData (with comprobante appended), send directly
    if (data instanceof FormData) {
      return api.post(`/gastos/facturas/${facturaId}/pagar`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    // If comprobante is passed separately, build FormData
    if (comprobante) {
      const formData = new FormData();
      Object.keys(data).forEach(key => formData.append(key, data[key]));
      formData.append('comprobante', comprobante);
      return api.post(`/gastos/facturas/${facturaId}/pagar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post(`/gastos/facturas/${facturaId}/pagar`, data);
  },
  aplicarPagoMultiple: (formData) => api.post('/gastos/pagos/multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  desaplicarPago: (pagoId, motivo) => api.post(`/gastos/pagos/${pagoId}/desaplicar`, { motivo }),
  notificarPagoProveedor: (pagoId) => api.post(`/gastos/pagos/${pagoId}/notificar`),
  // Nueva notificación CxP — soporta multi-FE, destinatarios custom, mensaje editable.
  getProveedorNotifDestinatarios: (proveedorId) => api.get(`/gastos/cxp/proveedor/${proveedorId}/notif-destinatarios`),
  getCxpPagoDetalle: (params) => api.get(`/gastos/cxp/pago/detalle`, { params }),
  enviarNotificacionPagoProveedor: (payload) => api.post(`/gastos/cxp/pago/notificar`, payload),
  previewNotificacionPagoProveedor: (payload) => api.post(`/gastos/cxp/pago/notificar/preview`, payload),
  getCxpNotifConfig: () => api.get(`/gastos/cxp/pago/notificar/config`),
  saveCxpNotifConfig: (payload) => api.put(`/gastos/cxp/pago/notificar/config`, payload),
  downloadComprobante: (pagoId) => api.get(`/gastos/pagos/${pagoId}/comprobante`, { responseType: 'blob' }),

  // Pagos Parent (transacciones padre agrupadas)
  listPagosParent: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/pagos-parent${queryString ? `?${queryString}` : ''}`);
  },
  getPagoParent: (id) => api.get(`/gastos/pagos-parent/${id}`),
  getPagoParentComprobante: (id) => api.get(`/gastos/pagos-parent/${id}/comprobante`, { responseType: 'blob' }),
  reversarPagoParent: (id, motivo) => api.delete(`/gastos/pagos-parent/${id}`, { data: { motivo } }),

  // Operaciones por lotes (batch)
  batchAceptar: (ids, tipoPago = 'credito') => api.post('/gastos/facturas/batch/aceptar', { ids, tipoPago }),
  batchRechazar: (ids, motivo) => api.post('/gastos/facturas/batch/rechazar', { ids, motivo }),
  batchEliminar: (ids) => api.post('/gastos/facturas/batch/eliminar', { ids }),
  batchDescargarXML: (ids, tipo = 'documento') => api.post('/gastos/facturas/batch/descargar-xml', { ids, tipo }, { responseType: 'blob' }),

  // Categorización de gastos para crédito fiscal IVA
  getCategorias: () => api.get('/gastos/categorias'),
  getFacturaLineas: (facturaId) => api.get(`/gastos/facturas/${facturaId}/lineas`),
  categorizarLinea: (facturaId, lineaId, data) => api.put(`/gastos/facturas/${facturaId}/lineas/${lineaId}/categorizar`, data),
  categorizarFactura: (facturaId, data) => api.post(`/gastos/facturas/${facturaId}/categorizar-todas`, data),
  batchCategorizar: (ids, data) => api.post('/gastos/facturas/batch/categorizar', { ids, ...data }),
  reparseCabys: (facturaId) => api.post(`/gastos/facturas/${facturaId}/reparse-cabys`),
  getResumenCategorizacion: (facturaId) => api.get(`/gastos/facturas/${facturaId}/resumen-categorizacion`),

  // Reportes
  getReportePendientes: () => api.get('/gastos/reporte/pendientes'),
  getReporteVencidas: () => api.get('/gastos/reporte/vencidas'),
  getReportePagosPeriodo: (fechaDesde, fechaHasta) =>
    api.get(`/gastos/reporte/pagos-periodo?fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}`),

  // Email Config (for receiving invoices)
  getGastosEmailConfig: () => api.get('/gastos/email-config'),
  saveGastosEmailConfig: (data) => api.post('/gastos/email-config', data),
  testGastosEmailConnection: () => api.post('/gastos/email-config/test'),
  toggleGastosEmailActive: () => api.post('/gastos/email-config/toggle'),
  deleteGastosEmailConfig: () => api.delete('/gastos/email-config'),
  createWebhook: () => api.post('/gastos/email-config/webhook'),
  deleteWebhook: () => api.delete('/gastos/email-config/webhook'),

  // Email Import
  getEmailImportStatus: () => api.get('/gastos/email-import/status'),
  getEmailImportHistory: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/email-import/history${queryString ? `?${queryString}` : ''}`);
  },
  clearEmailImportHistory: () => api.delete('/gastos/email-import/history'),
  checkEmailNow: () => api.post('/gastos/email-import/check'),

  // IVA - Créditos y Débitos
  getIvaPeriodo: (periodo) => api.get(`/gastos/iva/periodo/${periodo}`),
  getIvaAnual: (anio) => api.get(`/gastos/iva/anual/${anio}`),
  getIvaCreditosPendientes: (periodo) => api.get(`/gastos/iva/creditos-pendientes/${periodo}`),
  recalcularIvaPeriodo: (periodo) => api.post(`/gastos/iva/recalcular/${periodo}`),
  getIvaDashboard: () => api.get('/gastos/iva/dashboard'),

  // IVA - Exportación
  exportarIvaPeriodo: (periodo, formato = 'xlsx') =>
    api.get(`/gastos/iva/exportar/${periodo}?formato=${formato}`, { responseType: 'blob' }),
  exportarIvaAnual: (anio) =>
    api.get(`/gastos/iva/exportar-anual/${anio}`, { responseType: 'blob' }),

  // Factura Electrónica de Compra (FEC)
  listFEC: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/fec${queryString ? `?${queryString}` : ''}`);
  },
  getFEC: (id) => api.get(`/gastos/fec/${id}`),
  createFEC: (data) => api.post('/gastos/fec', data),
  checkFECStatus: (id) => api.get(`/gastos/fec/${id}/status`),
  downloadFECXml: (id) => api.get(`/gastos/fec/${id}/xml`, { responseType: 'blob' }),
  downloadFECXmlRespuesta: (id) => api.get(`/gastos/fec/${id}/xml-respuesta`, { responseType: 'blob' }),
  deleteFEC: (id) => api.delete(`/gastos/fec/${id}`),
  corregirCondicion: (id, nuevaCondicion) => api.patch(`/gastos/facturas/${id}/corregir-condicion`, { nuevaCondicion }),
  pagadoPor: (id, datos) => api.post(`/gastos/facturas/${id}/pagado-por`, datos),
  // Corregir condición de FEC (otra tabla — facturas_compra_emitidas)
  corregirCondicionFEC: (id, nuevaCondicion, plazoCredito = 30) =>
    api.patch(`/gastos/fec/${id}/corregir-condicion`, { nuevaCondicion, plazoCredito }),
  exportFECExcel: (params = {}) => api.get(`/gastos/fec/export/excel`, { params, responseType: 'blob' }),
  exportFECPdf: (params = {}) => api.get(`/gastos/fec/export/pdf`, { params, responseType: 'blob' }),
  exportarHistorialPagos: (formato, params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/pagos/export/${formato}${queryString ? `?${queryString}` : ''}`, { responseType: 'blob' });
  },
  extractFECPdf: (formData) => api.post('/gastos/fec/extract-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  saveCabysMapping: (data) => api.post('/gastos/fec/cabys-mapping', data),
  getAiPrompt: () => api.get('/gastos/fec/ai-prompt'),
  saveAiPrompt: (prompt) => api.put('/gastos/fec/ai-prompt', { prompt }),
  reenviarFEC: (id, data) => api.post(`/gastos/fec/${id}/reenviar`, data),
  viewFECPdf: (id) => api.get(`/gastos/fec/${id}/pdf`, { responseType: 'blob' }),

  // Consultar proveedor en Hacienda
  consultarProveedorHacienda: (identificacion) => api.get(`/gastos/proveedores/consultar/${identificacion}`),

  // Control IVA - Análisis Jerárquico
  // Créditos (compras)
  getControlIvaAnalisis: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/control-iva/analisis${queryString ? `?${queryString}` : ''}`);
  },
  getControlIvaPeriodos: () => api.get('/gastos/control-iva/periodos'),

  // Débitos (ventas)
  getControlIvaDebitos: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/gastos/control-iva/analisis-debitos${queryString ? `?${queryString}` : ''}`);
  },
  getControlIvaPeriodosDebitos: () => api.get('/gastos/control-iva/periodos-debitos'),
  getAnulacionesCruzadas: (periodo) => api.get(`/gastos/control-iva/anulaciones-cruzadas?periodo=${encodeURIComponent(periodo)}`),

  // IVA Inteligente - Dashboard simplificado con AI
  getIvaInteligenteResumen: (periodo) => api.get(`/gastos/iva-inteligente/resumen/${periodo}`),
  categorizarAutomatico: (facturaIds = null) => api.post('/gastos/iva-inteligente/categorizar-auto', { facturaIds }),
  getSinCategorizar: () => api.get('/gastos/iva-inteligente/sin-categorizar'),
  getProrrataSugerida: (anio) => api.get(`/gastos/iva-inteligente/prorrata-sugerida/${anio}`),
  actualizarProrrata: (prorrata) => api.put('/gastos/iva-inteligente/prorrata', { prorrata }),

  // IVA Inteligente - Detalle de movimientos
  getDetalleDebitos: (periodo) => api.get(`/gastos/iva-inteligente/detalle/debitos/${periodo}`),
  getDetalleCreditos: (periodo) => api.get(`/gastos/iva-inteligente/detalle/creditos/${periodo}`),
  getDetalleFEC: (periodo) => api.get(`/gastos/iva-inteligente/detalle/fec/${periodo}`),
  // Exports del detalle (Excel/PDF) — vista = debitos | creditos | fec
  exportIvaDetalle: (vista, periodo, formato) =>
    api.get(`/gastos/iva-inteligente/detalle-export/${vista}?periodo=${periodo}&formato=${formato}`, { responseType: 'blob' }),
  categorizarLineaIva: (lineaId, categoria) => api.put(`/gastos/iva-inteligente/linea/${lineaId}/categorizar`, categoria),
};

// ====================
// CXC (Cuentas por Cobrar) API
// ====================
export const cxcApi = {
  // Facturas pendientes de cobro
  listFacturas: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/cxc/facturas${queryString ? `?${queryString}` : ''}`);
  },
  getFactura: (id) => api.get(`/cxc/facturas/${id}`),

  // Pagos
  aplicarPago: (facturaId, data, comprobante = null) => {
    if (comprobante) {
      const formData = new FormData();
      Object.keys(data).forEach(key => formData.append(key, data[key]));
      formData.append('comprobante', comprobante);
      return api.post(`/cxc/facturas/${facturaId}/pagar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post(`/cxc/facturas/${facturaId}/pagar`, data);
  },
  enviarEstadoCuenta: (data) => api.post('/cxc/estado-cuenta/email', data),
  previewEstadoCuenta: (data) => api.post('/cxc/estado-cuenta/email/preview', data),
  // Saldo a favor de clientes
  saldoFavorResumen: () => api.get('/cxc/saldo-favor/resumen'),
  saldoFavor: (receptorId) => api.get(`/cxc/saldo-favor/${receptorId}`),
  registrarSaldoFavor: (formData) => api.post('/cxc/saldo-favor', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  aplicarSaldoFavor: (data) => api.post('/cxc/saldo-favor/aplicar', data),
  devolverSaldoFavor: (data) => api.post('/cxc/saldo-favor/devolver', data),
  getEstadoCuentaConfig: () => api.get('/cxc/estado-cuenta/config'),
  saveEstadoCuentaConfig: (data) => api.put('/cxc/estado-cuenta/config', data),
  // Destinatarios guardados por receptor (memoria para estado de cuenta)
  getReceptorNotifDestinatarios: (receptorId) => api.get(`/cxc/receptor/${receptorId}/notif-destinatarios`),
  aplicarPagoMultiple: (formData) => api.post('/cxc/pagos/multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Cobros (parent transactions)
  listCobros: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/cxc/cobros${queryString ? `?${queryString}` : ''}`);
  },
  getCobro: (id) => api.get(`/cxc/cobros/${id}`),
  getCobroComprobante: (id) => api.get(`/cxc/cobros/${id}/comprobante`, { responseType: 'blob' }),

  // Reversar cobro completo
  reversarCobro: (cobroId, motivo) => api.delete(`/cxc/cobros/${cobroId}`, { data: { motivo } }),

  // Legacy (mantener por compatibilidad)
  listPagos: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/cxc/cobros${queryString ? `?${queryString}` : ''}`);
  },
  reversarPago: (pagoId, motivo) => api.delete(`/cxc/pagos/${pagoId}`, { data: { motivo } }),
  // `getComprobante` apuntaba a `/cxc/comprobantes/:id`, que no existe. La puerta
  // real es la de abajo; ninguna pantalla usaba la otra.
  getComprobanteDeCobro: (cobroId) =>
    api.get(`/cxc/cobros/${cobroId}/comprobante`, { responseType: 'blob' }),

  // Exportar historial
  exportarHistorial: (formato, params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/cxc/cobros/export/${formato}${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Resumen/Dashboard
  getResumen: () => api.get('/cxc/resumen'),

  // Export facturas pendientes
  exportFacturasExcel: (queryString = '') => {
    return api.get(`/cxc/facturas/export/excel${queryString ? `?${queryString}` : ''}`, { responseType: 'blob' });
  },
  exportFacturasPdf: (queryString = '') => {
    return api.get(`/cxc/facturas/export/pdf${queryString ? `?${queryString}` : ''}`, { responseType: 'blob' });
  },
};

// Recurring Invoices API
export const recurringApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/recurring${queryString ? `?${queryString}` : ''}`);
  },

  getById: (id) => api.get(`/recurring/${id}`),

  create: (data) => api.post('/recurring', data),

  update: (id, data) => api.put(`/recurring/${id}`, data),

  delete: (id) => api.delete(`/recurring/${id}`),

  activate: (id) => api.post(`/recurring/${id}/activate`),

  deactivate: (id) => api.post(`/recurring/${id}/deactivate`),

  generate: (id, submit = false) => api.post(`/recurring/${id}/generate`, { submit }),

  getHistory: (id) => api.get(`/recurring/${id}/history`),

  // Generate drafts for all active templates due today
  generateDrafts: () => api.post('/recurring/generate-drafts'),

  // Crear plantilla recurrente a partir de un documento ACEPTADO existente
  createFromDocumento: (docId, config) => api.post(`/recurring/from-documento/${docId}`, config),

  // Config del reporte por correo (destinatarios) que se envía tras cada ciclo
  getReporteConfig: () => api.get('/recurring/reporte-config'),
  saveReporteConfig: (data) => api.put('/recurring/reporte-config', data),
  sendReporteTest: () => api.post('/recurring/reporte-config/test'),
};

// ====================
// PARÁMETROS API (Configuración del sistema)
// ====================
export const parametrosApi = {
  // Medios de Pago
  listMediosPago: () => api.get('/parametros/medios-pago'),
  listMediosPagoActivos: () => api.get('/parametros/medios-pago/activos'),
  createMedioPago: (data) => api.post('/parametros/medios-pago', data),
  updateMedioPago: (id, data) => api.put(`/parametros/medios-pago/${id}`, data),
  deleteMedioPago: (id) => api.delete(`/parametros/medios-pago/${id}`),

  // Bancos
  listBancos: () => api.get('/parametros/bancos'),
  listBancosActivos: () => api.get('/parametros/bancos/activos'),
  createBanco: (data) => api.post('/parametros/bancos', data),
  updateBanco: (id, data) => api.put(`/parametros/bancos/${id}`, data),
  deleteBanco: (id) => api.delete(`/parametros/bancos/${id}`),

  // Días de Crédito
  listDiasCredito: () => api.get('/parametros/dias-credito'),
  listDiasCreditoActivos: () => api.get('/parametros/dias-credito/activos'),
  createDiasCredito: (data) => api.post('/parametros/dias-credito', data),
  updateDiasCredito: (id, data) => api.put(`/parametros/dias-credito/${id}`, data),
  deleteDiasCredito: (id) => api.delete(`/parametros/dias-credito/${id}`),

  // Tipos de Exoneración
  listTiposExoneracion: () => api.get('/parametros/tipos-exoneracion'),
  getTipoExoneracion: (id) => api.get(`/parametros/tipos-exoneracion/${id}`),
  createTipoExoneracion: (data) => api.post('/parametros/tipos-exoneracion', data),
  updateTipoExoneracion: (id, data) => api.put(`/parametros/tipos-exoneracion/${id}`, data),
  deleteTipoExoneracion: (id) => api.delete(`/parametros/tipos-exoneracion/${id}`),

  // Instituciones de Exoneración
  listInstitucionesExoneracion: () => api.get('/parametros/instituciones-exoneracion'),

  // Clasificaciones por Ley Especial (variantes del código '03' de exoneración: INCOPESCA, MAG, CONARE, SINAES, Universidades, etc.)
  listClasificacionesLeyEspecial: () => api.get('/parametros/clasificaciones-ley-especial'),
  createClasificacionLeyEspecial: (data) => api.post('/parametros/clasificaciones-ley-especial', data),
  updateClasificacionLeyEspecial: (id, data) => api.put(`/parametros/clasificaciones-ley-especial/${id}`, data),
  deleteClasificacionLeyEspecial: (id) => api.delete(`/parametros/clasificaciones-ley-especial/${id}`),

  // Tarifas IVA
  listTarifasIva: () => api.get('/parametros/tarifas-iva'),
  getTarifaIva: (id) => api.get(`/parametros/tarifas-iva/${id}`),
  createTarifaIva: (data) => api.post('/parametros/tarifas-iva', data),
  updateTarifaIva: (id, data) => api.put(`/parametros/tarifas-iva/${id}`, data),
  deleteTarifaIva: (id) => api.delete(`/parametros/tarifas-iva/${id}`),

  // Emisores (para selects)
  listEmisores: () => api.get('/parametros/emisores'),
};

// ====================
// SYSTEM STATUS API
// ====================
export const systemApi = {
  // Health dashboard
  getHealth: () => api.get('/system/health'),

  // Email logs
  getEmailLogs: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/system/email-logs${queryString ? `?${queryString}` : ''}`);
  },

  // Background jobs status
  getJobs: () => api.get('/system/jobs'),

  // Sync exchange rate for this tenant
  syncExchangeRate: () => api.post('/system/sync-exchange-rate'),

  // Get exchange rate config and logs for this tenant
  getExchangeRateConfig: () => api.get('/system/exchange-rate-config'),

  // Update exchange rate config for this tenant
  updateExchangeRateConfig: (config) => api.put('/system/exchange-rate-config', config),

  // Optimize fragmented database tables
  optimizeTables: () => api.post('/system/optimize-tables'),
};

// ====================
// RESPALDOS API (solo los de esta empresa)
// ====================
// Tema de la consola (por cuenta): finance360 | finance360-solido | clasico
export const aparienciaApi = {
  leer: () => api.get('/apariencia'),
  cambiar: (tema) => api.put('/apariencia', { tema })
};

// Integraciones de la empresa con la suite (Configuración › Integraciones)
export const integracionesApi = {
  payroll: () => api.get('/integraciones/payroll'),
  guardarPayroll: (data) => api.put('/integraciones/payroll', data),
  probarPayroll: () => api.post('/integraciones/payroll/probar'),
  jefesPayroll: () => api.get('/integraciones/payroll/departamentos'),
  quitarPayroll: () => api.delete('/integraciones/payroll')
};

export const presupuestoApi = {
  listar: () => api.get('/presupuesto'),
  crear: (data) => api.post('/presupuesto', data),
  obtener: (id) => api.get(`/presupuesto/${id}`),
  actualizarParametros: (id, data) => api.put(`/presupuesto/${id}/parametros`, data),
  guardarCatalogo: (tipo, items) => api.put(`/presupuesto/catalogos/${tipo}`, { items }),
  crearPuesto: (id, data) => api.post(`/presupuesto/${id}/puestos`, data),
  actualizarPuesto: (id, puestoId, data) => api.put(`/presupuesto/${id}/puestos/${puestoId}`, data),
  eliminarPuesto: (id, puestoId) => api.delete(`/presupuesto/${id}/puestos/${puestoId}`),
  crearFila: (id, data) => api.post(`/presupuesto/${id}/filas`, data),
  actualizarFila: (id, filaId, data) => api.put(`/presupuesto/${id}/filas/${filaId}`, data),
  eliminarFila: (id, filaId) => api.delete(`/presupuesto/${id}/filas/${filaId}`),
  eliminar: (id) => api.delete(`/presupuesto/${id}`),
  actualizarCelda: (id, filaId, campo, data) => api.put(`/presupuesto/${id}/filas/${filaId}/celdas/${campo}`, data),
  historialCelda: (id, filaId, campo) => api.get(`/presupuesto/${id}/filas/${filaId}/celdas/${campo}/historial`),
  copiarCelda: (id, filaId, campo, hastaMes) => api.post(`/presupuesto/${id}/filas/${filaId}/celdas/${campo}/copiar`, { hastaMes }),
  actualizarVenta: (id, mes, data) => api.put(`/presupuesto/${id}/ventas/${mes}`, data),
  tomarVentasDeFacturacion: (id, meses) => api.post(`/presupuesto/${id}/ventas/tomar-facturacion`, { meses }),
  guardarPersonal: (id, data) => api.put(`/presupuesto/${id}/personal`, data),
  crearProblema: (id, data) => api.post(`/presupuesto/${id}/problemas`, data),
  actualizarProblema: (id, pid, data) => api.put(`/presupuesto/${id}/problemas/${pid}`, data),
  eliminarProblema: (id, pid) => api.delete(`/presupuesto/${id}/problemas/${pid}`),
  listarVersiones: (id, moneda) => api.get(`/presupuesto/${id}/versiones`, { params: moneda ? { moneda } : {} }),
  guardarVersion: (id, nombre) => api.post(`/presupuesto/${id}/versiones`, { nombre }),
  // Modelo estándar
  cambiarEstado: (id, estado) => api.put(`/presupuesto/${id}/estado`, { estado }),
  catalogoContable: () => api.get('/presupuesto/catalogo-contable'),
  ejecucion: (id, hasta, moneda) => api.get(`/presupuesto/${id}/ejecucion`, { params: { hasta, ...(moneda ? { moneda } : {}) } }),
  repartir: (id, filaId, data) => api.post(`/presupuesto/${id}/filas/${filaId}/repartir`, data),
  ajustarFila: (id, filaId, pct) => api.post(`/presupuesto/${id}/filas/${filaId}/ajustar`, { pct }),
  desglosarPuesto: (id, filaId, data) => api.post(`/presupuesto/${id}/filas/${filaId}/desglosar`, data),
  cuentasNomina: (cuentaId) => api.get(`/presupuesto/cuentas-nomina?cuentaId=${cuentaId}`),
  departamentosPayroll: () => api.get('/presupuesto/payroll/departamentos'),
  vistaPreviaPayroll: (id, agrupar) => api.get(`/presupuesto/${id}/payroll/vista-previa`, { params: { agrupar } }),
  traerDePayroll: (id, data) => api.post(`/presupuesto/${id}/payroll/traer`, data),
  // Modificaciones presupuestarias y comparación (P8)
  modificaciones: (id, moneda) => api.get(`/presupuesto/${id}/modificaciones`, { params: moneda ? { moneda } : {} }),
  pedirModificacion: (id, data) => api.post(`/presupuesto/${id}/modificaciones`, data),
  aprobarModificacion: (id, mid) => api.post(`/presupuesto/${id}/modificaciones/${mid}/aprobar`),
  rechazarModificacion: (id, mid, comentario) => api.post(`/presupuesto/${id}/modificaciones/${mid}/rechazar`, { comentario }),
  comparar: (id, con, moneda) => api.get(`/presupuesto/${id}/comparar`, { params: { con, ...(moneda ? { moneda } : {}) } }),
  // Presupuesto por departamento (P7)
  departamentos: (id, hasta, moneda) => api.get(`/presupuesto/${id}/departamentos`, { params: { hasta, ...(moneda ? { moneda } : {}) } }),
  iniciarDepartamentos: (id) => api.post(`/presupuesto/${id}/departamentos/iniciar`),
  usuariosEmpresa: (id) => api.get(`/presupuesto/${id}/departamentos/usuarios`),
  responsableDepartamento: (id, depto, usuarioId) => api.put(`/presupuesto/${id}/departamentos/${encodeURIComponent(depto)}/responsable`, { usuarioId }),
  enviarDepartamento: (id, depto) => api.post(`/presupuesto/${id}/departamentos/${encodeURIComponent(depto)}/enviar`),
  aceptarDepartamento: (id, depto) => api.post(`/presupuesto/${id}/departamentos/${encodeURIComponent(depto)}/aceptar`),
  devolverDepartamento: (id, depto, comentario) => api.post(`/presupuesto/${id}/departamentos/${encodeURIComponent(depto)}/devolver`, { comentario }),
  plantillaDepartamento: (id, depto) => api.get(`/presupuesto/${id}/departamentos/${encodeURIComponent(depto)}/plantilla`, { responseType: 'blob' }),
  subirPlantillaDepartamento: (id, depto, archivo) => { const f = new FormData(); f.append('archivo', archivo); return api.post(`/presupuesto/${id}/departamentos/${encodeURIComponent(depto)}/plantilla`, f, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  listarMapeos: () => api.get('/presupuesto/mapeos'),
  guardarMapeos: (items) => api.put('/presupuesto/mapeos', { items }),
  aplicarMapeos: (id) => api.post(`/presupuesto/${id}/mapeos/aplicar`),
  exportarExcel: (id) => api.get(`/presupuesto/${id}/excel`, { responseType: 'blob' }),
  importarExcel: (id, archivo, modo) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    fd.append('modo', modo);
    return api.post(`/presupuesto/${id}/excel`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  controlConfig: () => api.get('/presupuesto/control/config'),
  guardarControlConfig: (data) => api.put('/presupuesto/control/config', data),
  controlEventos: () => api.get('/presupuesto/control/eventos'),
  alertas: (id, moneda) => api.get(`/presupuesto/${id}/alertas`, { params: moneda ? { moneda } : {} }),
  reproyectar: (id, hasta) => api.post(`/presupuesto/${id}/estimado`, { hasta }),
  recurrentes: (id) => api.get(`/presupuesto/${id}/recurrentes`),
  consolidadoOpciones: (anio) => api.get('/presupuesto/consolidado/opciones', { params: { anio } }),
  consolidado: (params) => api.get('/presupuesto/consolidado', { params }),
  balance: (id, moneda) => api.get(`/presupuesto/${id}/balance`, { params: moneda ? { moneda } : {} }),
  guardarParametrosBalance: (id, body) => api.put(`/presupuesto/${id}/balance/parametros`, body),
  inversiones: (id, moneda) => api.get(`/presupuesto/${id}/inversiones`, { params: moneda ? { moneda } : {} }),
  guardarInversion: (id, body, invId) => (invId ? api.put(`/presupuesto/${id}/inversiones/${invId}`, body) : api.post(`/presupuesto/${id}/inversiones`, body)),
  eliminarInversion: (id, invId) => api.delete(`/presupuesto/${id}/inversiones/${invId}`),
  depreciacionExistentes: (id, incluir) => api.put(`/presupuesto/${id}/inversiones/existentes`, { incluir }),
  guardarRecurrentes: (id, body) => api.put(`/presupuesto/${id}/recurrentes`, body),
  revisarAlertas: (id) => api.post(`/presupuesto/${id}/alertas/revisar`),
  verificarOC: (data) => api.post('/presupuesto/control/verificar', data),
};

export const respaldosApi = {
  listar: () => api.get('/respaldos'),
  crear: (alcance = 'empresa') => api.post('/respaldos', { alcance }),
  descargar: (id) => api.get(`/respaldos/${id}/descargar`, { responseType: 'blob', timeout: 0 }),
  restaurar: (id, confirmacion) => api.post(`/respaldos/${id}/restaurar`, { confirmacion }),
  eliminar: (id) => api.delete(`/respaldos/${id}`),
  eliminarTodos: (alcance) => api.delete('/respaldos', { params: { alcance } }),
  guardarDestino: (data) => api.put('/respaldos/destino', data),
  probarDestino: (data) => api.post('/respaldos/destino/probar', data),
  guardarProgramacion: (data) => api.put('/respaldos/programacion', data),
};

// ====================
// CONTABILIDAD API
// ====================
export const contabilidadApi = {
  // Dashboard
  getDashboard: () => api.get('/contabilidad/dashboard'),

  // Centros de costo con los que se puede filtrar un estado financiero (item 028)
  getCentrosCosto: () => api.get('/contabilidad/centros-costo'),

  // ── Dimensiones contables (item 117) ──────────────────────────────────────
  //
  // El catalogo vive en Finance. `getDimensionCatalogo` acepta `aFecha`: devuelve
  // los valores vigentes A ESA FECHA, que es lo que se ofrece al imputar un
  // asiento de ese dia — no los vigentes hoy. Sin `aFecha` vienen todos, que es
  // lo que mira quien administra el catalogo, incluidos los cerrados.
  getDimensiones: (incluirInactivos = false) =>
    api.get(`/contabilidad/dimensiones${incluirInactivos ? '?incluirInactivos=true' : ''}`),
  createDimension: (data) => api.post('/contabilidad/dimensiones', data),
  updateDimension: (id, data) => api.put(`/contabilidad/dimensiones/${id}`, data),
  deleteDimension: (id) => api.delete(`/contabilidad/dimensiones/${id}`),
  getDimensionCatalogo: (id, params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/dimensiones/${id}/catalogo${qs ? `?${qs}` : ''}`);
  },
  createDimensionValor: (id, data) => api.post(`/contabilidad/dimensiones/${id}/catalogo`, data),
  updateDimensionValor: (valorId, data) => api.put(`/contabilidad/dimensiones/catalogo/${valorId}`, data),
  deleteDimensionValor: (valorId) => api.delete(`/contabilidad/dimensiones/catalogo/${valorId}`),

  // ── Activos fijos (sprint 8.5) ────────────────────────────────────────────
  //
  // `getActivo` trae la ficha, la configuración por libro, el calendario y los
  // movimientos en UNA llamada: la pantalla los muestra juntos, y cuatro
  // llamadas serían cuatro estados que se pueden desincronizar justo donde se
  // notaría — el calendario contra la ficha.
  getActivoClases: (incluirInactivas = false) =>
    api.get(`/contabilidad/activos/clases${incluirInactivas ? '?incluirInactivas=true' : ''}`),
  createActivoClase: (data) => api.post('/contabilidad/activos/clases', data),
  getActivoLibros: () => api.get('/contabilidad/activos/libros'),

  getActivos: (params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/activos${qs ? `?${qs}` : ''}`);
  },
  getActivo: (id, params = {}) => {
    const qs = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/activos/${id}${qs ? `?${qs}` : ''}`);
  },
  createActivo: (data) => api.post('/contabilidad/activos', data),
  ponerActivoEnMarcha: (id, fecha) =>
    api.post(`/contabilidad/activos/${id}/puesta-en-marcha`, { fecha }),
  recalcularActivo: (id) => api.post(`/contabilidad/activos/${id}/recalcular`),
  trasladarActivo: (id, data) => api.post(`/contabilidad/activos/${id}/trasladar`, data),
  darDeBajaActivo: (id, data) => api.post(`/contabilidad/activos/${id}/baja`, data),

  // La importación, en tres pasos. El del medio no escribe nada: lo que importa
  // de un archivo de 300 filas es saber cuáles NO van a entrar y por qué, antes
  // de tocar la base.
  getActivosPlantilla: () =>
    api.get('/contabilidad/activos/importar/plantilla', { responseType: 'blob' }),
  previsualizarActivos: (formData) =>
    api.post('/contabilidad/activos/importar/previsualizar', formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirmarActivos: (data) => api.post('/contabilidad/activos/importar/confirmar', data),

  // Plan de Cuentas
  getCuentas: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/cuentas${queryString ? `?${queryString}` : ''}`);
  },
  getCuenta: (id) => api.get(`/contabilidad/cuentas/${id}`),
  createCuenta: (data) => api.post('/contabilidad/cuentas', data),
  updateCuenta: (id, data) => api.put(`/contabilidad/cuentas/${id}`, data),
  deleteCuenta: (id) => api.delete(`/contabilidad/cuentas/${id}`),
  seedPlanCuentas: () => api.post('/contabilidad/cuentas/seed'),

  // Asientos Contables
  getAsientos: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/asientos${queryString ? `?${queryString}` : ''}`);
  },
  getAsiento: (id) => api.get(`/contabilidad/asientos/${id}`),
  createAsiento: (data) => api.post('/contabilidad/asientos', data),
  updateAsiento: (id, data) => api.put(`/contabilidad/asientos/${id}`, data),
  deleteAsiento: (id) => api.delete(`/contabilidad/asientos/${id}`),
  contabilizarAsiento: (id) => api.post(`/contabilidad/asientos/${id}/contabilizar`),
  contabilizarAsientosMasivo: (ids) => api.post('/contabilidad/asientos/contabilizar-masivo', { ids }),
  anularAsiento: (id, motivo) => api.post(`/contabilidad/asientos/${id}/anular`, { motivo }),
  getPendientes: () => api.get('/contabilidad/asientos/pendientes'),
  generarAsientosMasivo: (params) => api.post('/contabilidad/asientos/generar-masivo', params),

  // Libro Mayor
  getLibroMayorResumen: () => api.get('/contabilidad/mayor'),
  getLibroMayor: (cuentaId, params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/mayor/${cuentaId}${queryString ? `?${queryString}` : ''}`);
  },
  // `recalcularLibroMayor` llamaba `POST /contabilidad/mayor/recalcular`, que nunca
  // existio, y ninguna pantalla lo ofrecia. Se quita en vez de construir una
  // puerta que nadie pidio.

  // Estados Financieros
  getBalanceGeneral: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/estados/balance${queryString ? `?${queryString}` : ''}`);
  },
  getEstadoResultados: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/estados/resultados${queryString ? `?${queryString}` : ''}`);
  },
  getBalanceComprobacion: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/estados/comprobacion${queryString ? `?${queryString}` : ''}`);
  },
  getBalanceDiagnostico: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/estados/balance/diagnostico${queryString ? `?${queryString}` : ''}`);
  },

  // Exportar Estados Financieros
  exportEstadoResultados: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/estados/resultados/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
  exportBalanceGeneral: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/estados/balance/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },
  exportLibroMayor: (cuentaId, params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/mayor/${cuentaId}/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
  },

  // Configuración
  getConfig: () => api.get('/contabilidad/config'),
  updateConfig: (data) => api.put('/contabilidad/config', data),
  autoConfigurarCuentas: () => api.post('/contabilidad/config/auto'),

  // Categorías de Mapeos (Dinámico)
  getCategorias: (includeInactive = false) => api.get(`/contabilidad/mapeos/categorias?includeInactive=${includeInactive}`),
  createCategoria: (data) => api.post('/contabilidad/mapeos/categorias', data),
  updateCategoria: (id, data) => api.put(`/contabilidad/mapeos/categorias/${id}`, data),
  deleteCategoria: (id) => api.delete(`/contabilidad/mapeos/categorias/${id}`),

  // Mapeos Contables (Dinámico)
  getMapeos: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/mapeos${queryString ? `?${queryString}` : ''}`);
  },
  getMapeosGrouped: () => api.get('/contabilidad/mapeos?grouped=true'),
  getMapeo: (id) => api.get(`/contabilidad/mapeos/${id}`),
  getMapeoByCodigo: (categoria, codigo) => api.get(`/contabilidad/mapeos/buscar/${categoria}/${codigo}`),
  createMapeo: (data) => api.post('/contabilidad/mapeos', data),
  updateMapeo: (id, data) => api.put(`/contabilidad/mapeos/${id}`, data),
  deleteMapeo: (id) => api.delete(`/contabilidad/mapeos/${id}`),
  inicializarMapeos: () => api.post('/contabilidad/mapeos/inicializar'),
  getCuentaParaMapeo: (categoria, codigo) => api.get(`/contabilidad/mapeos/cuenta/${categoria}/${codigo}`),

  // Períodos
  getPeriodos: () => api.get('/contabilidad/periodos'),
  cerrarPeriodo: (id) => api.post(`/contabilidad/periodos/${id}/cerrar`),
  reabrirPeriodo: (id) => api.post(`/contabilidad/periodos/${id}/reabrir`),

  // Calendario fiscal y de gestión (S1)
  getCalendario: () => api.get('/contabilidad/calendario'),
  previsualizarCalendario: (data) => api.post('/contabilidad/calendario/previsualizar', data),
  guardarCalendario: (data) => api.put('/contabilidad/calendario', data),
  generarAnioPeriodos: (anio) => api.post('/contabilidad/calendario/generar', { anio }),

  // AI
  getAnomalias: () => api.get('/contabilidad/ai/anomalias'),
  sugerirCuenta: (data) => api.post('/contabilidad/ai/sugerir-cuenta', data),
  aprenderCorreccion: (data) => api.post('/contabilidad/ai/aprender', data),
  getPatrones: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/ai/patrones${queryString ? `?${queryString}` : ''}`);
  },
  deletePatron: (id) => api.delete(`/contabilidad/ai/patrones/${id}`),

  // Claude AI
  getClaudeStatus: () => api.get('/contabilidad/ai/claude/status'),
  explicarAsiento: (asientoId) => api.post('/contabilidad/ai/claude/explicar-asiento', { asientoId }),
  saveClaudeConfig: (data) => api.post('/contabilidad/ai/claude/config', data),
  testClaudeKey: (apiKey) => api.post('/contabilidad/ai/claude/test', { apiKey }),

  // Análisis contable con AI
  ejecutarAnalisisCompleto: () => api.post('/contabilidad/ai/analisis-completo'),
  getHistorialAnalisis: () => api.get('/contabilidad/ai/analisis/historial'),
  getAnalisis: (id) => api.get(`/contabilidad/ai/analisis/${id}`),

  // Diferencial cambiario realizado y valuación al cierre (S5)
  getDiferencialRealizado: (params) => api.get(`/contabilidad/diferencial-cambiario/realizado?${new URLSearchParams(cleanParams(params)).toString()}`),
  getDiferencialValuacion: (params) => api.get(`/contabilidad/diferencial-cambiario/valuacion?${new URLSearchParams(cleanParams(params)).toString()}`),
  getDiferencialComparativo: (params) => api.get(`/contabilidad/diferencial-cambiario/comparativo?${new URLSearchParams(cleanParams(params)).toString()}`),
  registrarValuacion: (data) => api.post('/contabilidad/diferencial-cambiario/valuacion/registrar', data),
  exportarDiferencial: (params) => api.get(`/contabilidad/diferencial-cambiario/excel?${new URLSearchParams(cleanParams(params)).toString()}`, { responseType: 'blob' }),

  // Flujo de caja proyectado (S4)
  getFlujoCaja: (params = {}) => api.get(`/contabilidad/flujo-caja?${new URLSearchParams(cleanParams(params)).toString()}`),
  getFlujosGuardados: () => api.get('/contabilidad/flujo-caja/guardadas'),
  guardarFlujoCaja: (data) => api.post('/contabilidad/flujo-caja/guardadas', data),
  compararFlujoCaja: (id) => api.get(`/contabilidad/flujo-caja/guardadas/${id}/comparar`),
  eliminarFlujoGuardado: (id) => api.delete(`/contabilidad/flujo-caja/guardadas/${id}`),
  exportarFlujoCaja: (params = {}) => api.get(`/contabilidad/flujo-caja/excel?${new URLSearchParams(cleanParams(params)).toString()}`, { responseType: 'blob' }),

  // Préstamos (S3)
  getPrestamos: () => api.get('/contabilidad/prestamos'),
  getPrestamo: (id) => api.get(`/contabilidad/prestamos/${id}`),
  crearPrestamo: (data) => api.post('/contabilidad/prestamos', data),
  simularPrestamo: (data) => api.post('/contabilidad/prestamos/simular', data),
  leerExcelPrestamo: (archivo) => { const f = new FormData(); f.append('archivo', archivo); return api.post('/contabilidad/prestamos/leer-excel', f, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  reemplazarTablaPrestamo: (id, tabla) => api.put(`/contabilidad/prestamos/${id}/tabla`, { tabla }),
  exportarPrestamo: (id) => api.get(`/contabilidad/prestamos/${id}/excel`, { responseType: 'blob' }),
  pagarCuotaPrestamo: (id, numero, data) => api.post(`/contabilidad/prestamos/${id}/cuotas/${numero}/pagar`, data),
  anularPagoPrestamo: (id, numero, motivo) => api.post(`/contabilidad/prestamos/${id}/cuotas/${numero}/anular`, { motivo }),
  reclasificarPrestamos: (data) => api.post('/contabilidad/prestamos/reclasificar', data),
  eliminarPrestamo: (id) => api.delete(`/contabilidad/prestamos/${id}`),

  // Bancos
  getBancos: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/bancos${queryString ? `?${queryString}` : ''}`);
  },
  getBanco: (id) => api.get(`/contabilidad/bancos/${id}`),
  createBanco: (data) => api.post('/contabilidad/bancos', data),
  updateBanco: (id, data) => api.put(`/contabilidad/bancos/${id}`, data),
  deleteBanco: (id) => api.delete(`/contabilidad/bancos/${id}`),

  // Movimientos Bancarios
  getBancoMovimientos: (id, params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/contabilidad/bancos/${id}/movimientos${queryString ? `?${queryString}` : ''}`);
  },
  getBancoResumen: (id) => api.get(`/contabilidad/bancos/${id}/resumen`),
  getBancoSugerencias: (id) => api.get(`/contabilidad/bancos/${id}/sugerencias`),
  importarExtracto: (id, formData) => api.post(`/contabilidad/bancos/${id}/importar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  conciliarMovimiento: (movId, data) => api.post(`/contabilidad/bancos/movimientos/${movId}/conciliar`, data),
  descartarMovimiento: (movId) => api.put(`/contabilidad/bancos/movimientos/${movId}/descartar`),
  crearAsientoDesdeMovimiento: (movId, data) => api.post(`/contabilidad/bancos/movimientos/${movId}/crear-asiento`, data),
  autoConciliar: (id, umbral = 90) => api.post(`/contabilidad/bancos/${id}/auto-conciliar`, { umbralConfianza: umbral }),

  // Reportes Hacienda
  getHaciendaPeriodos: (tipo) => api.get(`/contabilidad/hacienda/periodos?tipo=${tipo || 'mensual'}`),
  getD270: (periodo) => api.get(`/contabilidad/hacienda/d270/${periodo}`),
  getD104: (periodo) => api.get(`/contabilidad/hacienda/d104/${periodo}`),
  getD101: (anio) => api.get(`/contabilidad/hacienda/d101/${anio}`),
};

// ====================
// USUARIOS Y ROLES API
// ====================
export const usuariosApi = {
  // Usuarios
  list: () => api.get('/usuarios'),
  get: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),

  // Roles
  listRoles: () => api.get('/usuarios/roles/list'),
  listRolesActivos: () => api.get('/usuarios/roles/activos'),
  getRol: (id) => api.get(`/usuarios/roles/${id}`),
  createRol: (data) => api.post('/usuarios/roles', data),
  updateRol: (id, data) => api.put(`/usuarios/roles/${id}`, data),
  deleteRol: (id) => api.delete(`/usuarios/roles/${id}`),

  // Permisos
  listPermisos: () => api.get('/usuarios/permisos/list'),

  // Empresas (para asignar a usuarios)
  listEmpresas: () => api.get('/usuarios/empresas/list'),

  // Permisos de Proveedores (para CxP)
  getProveedoresPermitidos: (userId, emisorId) =>
    api.get(`/usuarios/${userId}/proveedores-permitidos?emisorId=${emisorId}`),
  saveProveedoresPermitidos: (userId, data) =>
    api.post(`/usuarios/${userId}/proveedores-permitidos`, data),
};

// ====================
// API CREDENTIALS
// ====================
export const apiCredentialsApi = {
  list: () => api.get('/api-credentials'),
  get: (id) => api.get(`/api-credentials/${id}`),
  getPermissions: () => api.get('/api-credentials/permissions'),
  getEmisores: () => api.get('/api-credentials/emisores'),
  create: (data) => api.post('/api-credentials', data),
  update: (id, data) => api.put(`/api-credentials/${id}`, data),
  delete: (id) => api.delete(`/api-credentials/${id}`),
  regenerateSecret: (id) => api.post(`/api-credentials/${id}/regenerate`),
};

export const cotizacionesApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/cotizaciones${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/cotizaciones/${id}`),
  create: (data) => api.post('/cotizaciones', data),
  update: (id, data) => api.put(`/cotizaciones/${id}`, data),
  delete: (id) => api.delete(`/cotizaciones/${id}`),
  updateEstado: (id, estado) => api.patch(`/cotizaciones/${id}/estado`, { estado }),
  downloadPdf: (id) => api.get(`/cotizaciones/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, options) => api.post(`/cotizaciones/${id}/email`, options),
  previewEmail: (id, options) => api.post(`/cotizaciones/${id}/email/preview`, options),
  convert: (id) => api.post(`/cotizaciones/${id}/convert`),
  getEmailConfig: () => api.get('/settings/email-cotizaciones'),
  saveEmailConfig: (data) => api.put('/settings/email-cotizaciones', data),
  duplicate: (id) => api.post(`/cotizaciones/${id}/duplicate`),
};

export const ordenesCompraApi = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(cleanParams(params)).toString();
    return api.get(`/ordenes-compra${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id) => api.get(`/ordenes-compra/${id}`),
  create: (data) => api.post('/ordenes-compra', data),
  update: (id, data) => api.put(`/ordenes-compra/${id}`, data),
  createFromCotizacion: (data) => api.post('/ordenes-compra/from-cotizacion', data),
  delete: (id) => api.delete(`/ordenes-compra/${id}`),
  updateEstado: (id, estado) => api.patch(`/ordenes-compra/${id}/estado`, { estado }),
  downloadPdf: (id) => api.get(`/ordenes-compra/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id, options) => api.post(`/ordenes-compra/${id}/email`, options),
  previewEmail: (id, options) => api.post(`/ordenes-compra/${id}/email/preview`, options),
  // Pagos (abonos parciales)
  listarPagos: (id) => api.get(`/ordenes-compra/${id}/pagos`),
  registrarPago: (id, formData) => api.post(`/ordenes-compra/${id}/pagos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  eliminarAbono: (id, pagoId) => api.delete(`/ordenes-compra/${id}/pagos/${pagoId}`),
  eliminarPago: (id) => api.delete(`/ordenes-compra/${id}/pago`),
  descargarComprobantePago: (id, pagoId) => api.get(`/ordenes-compra/${id}/pagos/${pagoId}/comprobante`, { responseType: 'blob' }),
  getByCotizacion: (cotizacionId) => api.get(`/cotizaciones/${cotizacionId}/ordenes-compra`),
  getLastByProveedor: (proveedorId) => api.get(`/ordenes-compra/last-by-proveedor/${proveedorId}`),
  duplicate: (id) => api.post(`/ordenes-compra/${id}/duplicate`),
};

// Finance360 Ops (Módulo Avanzado «ops»): análisis de borradores recurrentes de consumo
export const opsApi = {
  entrada: () => api.get('/ops/entrada'),
  borradoresConsumo: () => api.get('/ops/borradores-consumo'),
  analisis: (id, { guardada = false } = {}) => api.get(`/ops/borradores/${id}/analisis${guardada ? '?guardada=1' : ''}`, { timeout: 180000 }),
  config: () => api.get('/ops/config'),
  guardarConfig: (data) => api.put('/ops/config', data),
  mapeos: (id, data) => api.post(`/ops/borradores/${id}/mapeos`, data),
  actualizar: (id, cambios, nuevas = [], revisadas = []) => api.post(`/ops/borradores/${id}/actualizar`, { cambios, nuevas, revisadas }),
  cola: () => api.get('/ops/cola'),
  conCambios: () => api.get('/ops/con-cambios'),
  marcarLista: (id, lista = true) => api.put(`/ops/borradores/${id}/lista`, { lista }),
  actualizarPlantilla: (id) => api.post(`/ops/borradores/${id}/plantilla`),
  guardarRevision: (id, revision) => api.put(`/ops/borradores/${id}/revision`, revision),
  borrador: (id) => api.get(`/ops/borradores/${id}`),
  panel: () => api.get('/ops/panel', { timeout: 180000 }),
  tenants: (fuente) => api.get(`/ops/${fuente}/tenants`),
  conectar: (clienteId, fuente, directorio) => api.post(`/ops/clientes/${clienteId}/${fuente}/conectar`, directorio ? { directorio } : {}),
  vincular: (clienteId, fuente, tenantId) => api.post(`/ops/clientes/${clienteId}/${fuente}/vincular`, { tenantId }, { timeout: 120000 }),
};

export default api;
